import { Icon, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import { UserGet, UserInfo, UserPut } from "../../../../models/User"
import { copyToClipboard } from "../../../../utils/copyToClipboard"

const validationSchema = z.object({
  first: z.string().min(1, "First name is required"),
  last: z.string().min(1, "Last name is required"),
})

const returnURL = `/users`

export function UserDetails(props: {
  readonly user: UserGet
  readonly loggedUser: UserInfo
  readonly setSelectedUser: (updatedUser: UserGet) => void
}) {
  const { user, loggedUser, setSelectedUser } = props

  const formMethods = useForm<UserPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = formMethods

  useEffect(() => {
    reset({
      first: user.first,
      last: user.last,
    })
  }, [user, reset])

  const onSubmit = useCallback(
    async (values: UserPut) => {
      try {
        const res = await digitalManagerApi.put<UserGet>(`/users/${user.id}`, {
          first: values.first,
          last: values.last,
        })
        toast.success("User updated")
        setSelectedUser(res.data)
      } catch {
        toast.error("Cannot update user")
      }
    },
    [user.id, setSelectedUser]
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<UserPut, "first" | "last">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderTextFieldFirstName = useCallback(
    ({ field }: { field: ControllerRenderProps<UserPut, "first"> }) => (
      <MUITextField
        id="first"
        name={field.name}
        type="text"
        size={32}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.first?.message === "string"
            ? { first: { message: errors.first.message } }
            : undefined
        }
      />
    ),
    [errors.first, handleTextFieldChange]
  )

  const renderTextFieldLastName = useCallback(
    ({ field }: { field: ControllerRenderProps<UserPut, "last"> }) => (
      <MUITextField
        id="last"
        name={field.name}
        type="text"
        size={32}
        value={field.value ?? ""}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.last?.message === "string"
            ? { last: { message: errors.last.message } }
            : undefined
        }
      />
    ),
    [errors.last, handleTextFieldChange]
  )

  const copyToClipboardUsername = useCallback(() => {
    copyToClipboard(user.username)
  }, [user.username])

  const detailsContentProps: DiMaDetailsContentProps<UserPut> = {
    formMethods,
    canWrite: hasUserAccessToFeature(
      loggedUser.authorizations,
      DimaFeatureShortNameEnum.RM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "First name",
        displayItem: <div>{user.first}</div>,
        editItem: (
          <Controller name="first" control={control} render={renderTextFieldFirstName} />
        ),
      },
      {
        id: 1,
        label: "Last name",
        displayItem: <div>{user.last}</div>,
        editItem: (
          <Controller name="last" control={control} render={renderTextFieldLastName} />
        ),
      },
      {
        id: 2,
        label: "Email address",
        displayItem: (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ textTransform: "none" }}>{user.username}</div>
            <Icon
              iconName="icon_abb_dias icon-abb_copy-1"
              size={24}
              onClick={copyToClipboardUsername}
            />
          </div>
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
